import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  Button,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink,
  Loader,
  MenuInput,
  type MenuMultiOption,
  TabContent,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  A_KEY_OS,
  A_KEY_RO,
  A_KEY_SA,
  A_KEY_SA_UN,
  CF_CRT_MED,
  CF_INF_MED,
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_BLD,
  l,
  SIDEPANEL_OWNERS_COUNT_TO_SHOW,
} from '../../../../shared/constants';
import { useDebounce, useDebouncedFn } from '../../../../shared/hooks';
import {
  type PolicyApiItem,
  type ServiceIdentitySPItem,
} from '../../../../shared/types';
import { useGetPolicyListMutation } from '../../data-layer/serviceIdentityApi';
import { serviceIdentityActions } from '../../data-store/serviceIdentitySlice';
import {
  getDescCriticalText,
  getDescIsCritical,
  getNoDataText,
} from '../../utils';
import {
  SidePanelAccountItem,
  SidePanelOwnerItem,
  TypedChipInput,
} from '../Shared';

import './SummaryContent.css';

interface SummaryContentProps {
  activeTab: string;
  data: ServiceIdentitySPItem | null;
  serviceId?: number;
  policyId?: number | null;
  isLoading: boolean;
  isDisabled?: boolean;
  isError: boolean;
  onRefresh: () => void;
  setCurrentData: React.Dispatch<
    React.SetStateAction<ServiceIdentitySPItem | null>
  >;
  hasSummaryChanges: boolean;
}

/**
 * Renders the Summary tab content for the service identity side panel,
 * including overview, owners, password policy, and linked service accounts.
 * Shows a loader while fetching and an error state with a refresh option on failure.
 * @param props - {@link SummaryContentProps}
 * @param props.activeTab - The currently active tab value.
 * @param props.data - The fetched summary data, or null while loading.
 * @param props.serviceId - The ID of the service identity being viewed.
 * @param props.policyId - The ID of the associated password policy, if any.
 * @param props.isLoading - Whether the summary data is currently loading.
 * @param props.isDisabled - Whether interactive elements in the summary should be disabled.
 * @param props.isError - Whether the summary fetch encountered an error.
 * @param props.onRefresh - Callback invoked when the user clicks Refresh after an error.
 * @param props.setCurrentData - State setter for the mutable copy of summary data (used for tag edits).
 * @param props.hasSummaryChanges - Whether the user has unsaved changes to the summary.
 * @returns The summary tab content, or null if no serviceId is provided.
 */
function SummaryContent({
  activeTab,
  data,
  serviceId,
  policyId,
  isLoading,
  isDisabled,
  isError,
  onRefresh,
  setCurrentData,
  hasSummaryChanges,
}: SummaryContentProps) {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Description state
  const [description, setDescription] = useState('');

  // Policy state
  const [policyOptions, setPolicyOptions] = useState<
    (MenuMultiOption & { description: string | null })[]
  >([]);
  const [isPolicyLoading, setIsPolicyLoading] = useState(false);
  const [policySearchText, setPolicySearchText] = useState('');
  const policyCursorRef = useRef<string | number | null>(null);

  // Hooks
  const debouncedDescription = useDebounce<string>(description, 300);
  const [getPolicyList, { isError: isPolicyError }] =
    useGetPolicyListMutation();

  const loadPolicyOptions = useCallback(
    (search = policySearchText, isAppend = false) => {
      setIsPolicyLoading(true);

      // On search change, keep only checked options while loading
      if (!isAppend) {
        setPolicyOptions((prev) => prev.filter((o) => o.isChecked));
        policyCursorRef.current = null;
      }

      getPolicyList({
        search,
        max: 20,
        ...(policyCursorRef.current ? { cursor: policyCursorRef.current } : {}),
      })
        .unwrap()
        .then((response) => {
          const typedResponse = response as {
            success: boolean;
            data: PolicyApiItem[];
            cursor: string | null;
            msg: string;
          };

          if (!typedResponse.success) {
            throw new Error(typedResponse.msg);
          }

          policyCursorRef.current = typedResponse.cursor;

          setPolicyOptions((prev) => {
            const checkedOptions = prev.filter((o) => o.isChecked);
            const checkedValues = new Set(checkedOptions.map((o) => o.value));

            const newOptions = typedResponse.data.map((policy) => ({
              label: policy.name,
              value: String(policy.id),
              isChecked: checkedValues.has(String(policy.id)),
              description: policy.description,
            }));

            if (isAppend) {
              const existingValues = new Set(prev.map((o) => o.value));
              const uniqueNew = newOptions.filter(
                (o) => !existingValues.has(o.value)
              );

              return [...prev, ...uniqueNew];
            }

            const uncheckedNew = newOptions.filter(
              (o) => !checkedValues.has(o.value)
            );

            return [...checkedOptions, ...uncheckedNew];
          });
        })
        .catch((error) => {
          policyCursorRef.current = null;
          Logger.log('Error loading policy options', error);
        })
        .finally(() => {
          setIsPolicyLoading(false);
        });
    },
    [getPolicyList, policySearchText]
  );

  const debouncedLoadPolicyOptions = useDebouncedFn(loadPolicyOptions, 300);

  useEffect(() => {
    setCurrentData((prevData) => {
      if (!prevData) {
        return null;
      }

      return {
        ...prevData,
        description: debouncedDescription,
      };
    });
  }, [debouncedDescription, setCurrentData]);

  // Load policy options on mount
  useEffect(() => {
    loadPolicyOptions('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  // Sync policy selection with data. Depends on [data] (not data.passwordPolicy)
  // so it re-fires on Cancel, which restores the original object reference.
  useEffect(() => {
    if (data?.description) {
      setDescription(data.description);
    }

    if (data?.passwordPolicy) {
      const defaultOption = {
        label: data.passwordPolicy.name,
        value: String(data.passwordPolicy.id),
        isChecked: true,
        description: data.passwordPolicy.description,
      };

      setPolicyOptions((prev) => {
        const unchecked = prev.map((o) => ({ ...o, isChecked: false }));
        const alreadyExists = unchecked.some(
          (o) => o.value === defaultOption.value
        );

        if (alreadyExists) {
          return unchecked.map((o) =>
            o.value === defaultOption.value ? { ...o, isChecked: true } : o
          );
        }

        return [defaultOption, ...unchecked];
      });
    } else {
      setPolicyOptions((prev) => prev.map((o) => ({ ...o, isChecked: false })));
    }
    // data is the full object — effect re-runs on Cancel because
    // setCurrentSummaryData(summaryData) produces a new render with the original ref
  }, [data]);

  useEffect(() => {
    setDescription(data?.description ?? '');
  }, [serviceId, data?.description]);

  const handlePolicyScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target as HTMLElement;

      if (
        scrollHeight - scrollTop === clientHeight &&
        policyCursorRef.current !== null &&
        !isPolicyLoading
      ) {
        loadPolicyOptions(policySearchText, true);
      }
    },
    [isPolicyLoading, loadPolicyOptions, policySearchText]
  );

  const renderBody = useCallback(() => {
    if (isError) {
      return (
        <Box tag='div' className='ServiceIdentitySP-intermediateWrapper'>
          <Box tag='div' className='ServiceIdentitySP-errorMessageWrapper'>
            <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
            <Typography kind='body2' color={CF_CRT_MED}>
              <FormattedMessage
                id='DaArA8'
                defaultMessage="Data couldn't be retrieved."
                description='Error message shown when the history data fails to load in the service identity side panel'
              />
            </Typography>
          </Box>
          <Button kind='filled' size='small' onClick={onRefresh}>
            <FormattedMessage
              id='iqkpnY'
              defaultMessage='Refresh'
              description='Refresh'
            />
          </Button>
        </Box>
      );
    }

    if (isLoading || data === null) {
      return (
        <Box tag='div' className='ServiceIdentitySP-intermediateWrapper'>
          <Loader kind='dots' size='small' format='inline' color='brand' />
        </Box>
      );
    }

    const ownersCount = data.owners.length;
    const isRank1InactiveOnwer = Boolean(
      ownersCount > 0 &&
        data.owners.some(
          (o) =>
            o.type.toLowerCase() === 'user' &&
            String(o.rank) === '1' &&
            o.status.toLowerCase() !== 'active'
        )
    );
    const serviceAccountCount = data.serviceAccounts.length;
    const policyNoDataText = getNoDataText(
      isPolicyError,
      isPolicyLoading,
      '',
      policyOptions.length
    );

    return (
      <>
        <Box tag='div' className='SummaryContent-overviewContainer'>
          <Typography kind='h1' color={CF_NEU_BLD}>
            <FormattedMessage
              id='6+BhsD'
              defaultMessage='Overview'
              description='Overview'
            />
          </Typography>
          <Box tag='div' className='SummaryContent-overviewSubContainer'>
            <Box tag='div' className='SummaryContent-descContainer'>
              <Typography kind='body2' color={CF_NEU_STL}>
                <FormattedMessage
                  id='3T09+Q'
                  defaultMessage='Description'
                  description='Description'
                />
              </Typography>
              <InputField
                name='description'
                placeholder={intl.formatMessage({
                  id: 'YoUJI1',
                  defaultMessage: '-',
                  description: 'Hyphen',
                })}
                isDisabled={isDisabled}
                value={description}
                setValue={setDescription}
                shouldDisallowBrackets={false}
                isCritical={getDescIsCritical(debouncedDescription)}
                CriticalHelperText={
                  <InlineMessage
                    text={getDescCriticalText(debouncedDescription)}
                    colorTheme='critical'
                    size='small'
                    leftIcon={<Icon kind='AlertCriticalFilled' />}
                  />
                }
                className='SummaryContent-description'
              />
            </Box>
            <Box tag='div' className='SummaryContent-tagsContainer'>
              <div className='SummaryContent-tagsHeader'>
                <Typography kind='label' htmlFor='summaryTabTags'>
                  <FormattedMessage
                    id='ImtbH0'
                    defaultMessage='Tags'
                    description='Tags'
                  />
                </Typography>
                <Tooltip
                  trigger={
                    <Icon
                      kind='AlertInfoFilled'
                      size='xxSmall'
                      color={CF_INF_MED}
                    />
                  }
                  text={intl.formatMessage({
                    id: 'NZ8CXZ',
                    defaultMessage:
                      'Maximum 5 tags can be added. Use commas to add multiple.',
                    description:
                      'Maximum 5 tags can be added. Use commas to add multiple.',
                  })}
                  placement='top'
                />
              </div>
              <TypedChipInput
                isDisabled={isDisabled}
                currentTags={data.tags ?? []}
                setNewTags={(newTags) => {
                  setCurrentData((oldData) => {
                    if (oldData === null) {
                      return oldData;
                    }

                    return {
                      ...oldData,
                      tags: newTags,
                    };
                  });
                }}
              />
            </Box>
          </Box>
        </Box>
        {ownersCount > 0 && (
          <Box tag='div' className='SummaryContent-ownersContainer'>
            <Box tag='div' className='SummaryContent-ownersTitle'>
              <Typography kind='h3' color={CF_SEC_BLD}>
                <FormattedMessage
                  id='O8oJYy'
                  defaultMessage='Owners ({ownersCount})'
                  description='Owners heading label'
                  values={{ ownersCount }}
                />
              </Typography>
              {isRank1InactiveOnwer && (
                <Box tag='div' className='SummaryContent-ownersTitleInfo'>
                  <Tooltip
                    trigger={
                      <Icon
                        kind='AlertInfoFilled'
                        size='xxSmall'
                        color={CF_CRT_MED}
                      />
                    }
                    text={intl.formatMessage({
                      id: 'CtzSeH',
                      defaultMessage: 'No active owner with Rank 1 was found.',
                      description: 'No active owner with Rank 1 was found.',
                    })}
                    placement='top'
                  />
                </Box>
              )}
              <DSLink
                text={intl.formatMessage({
                  id: 'LR5Bk4',
                  defaultMessage: 'Manage Ownership',
                  description: 'Manage Ownership',
                })}
                isDisabled={isDisabled}
                kind='button'
                onClick={() => {
                  dispatch(
                    serviceIdentityActions.setActionManager({
                      isOpen: true,
                      id: serviceId,
                      action: A_KEY_OS,
                      currentOwners: data.owners,
                    })
                  );
                }}
              />
            </Box>
            <Box tag='div' className='SummaryContent-ownersList'>
              {data.owners
                .slice(0, SIDEPANEL_OWNERS_COUNT_TO_SHOW)
                .map((owner) => (
                  <SidePanelOwnerItem
                    key={owner.id}
                    intl={intl}
                    ownerName={owner.name}
                    ownerRank={owner.rank}
                    ownerStatus={owner.status}
                  />
                ))}
            </Box>
            {ownersCount - SIDEPANEL_OWNERS_COUNT_TO_SHOW > 0 && (
              <Box tag='div' className='SummaryContent-moreSection'>
                <ApplicationIcon
                  number={`+${ownersCount - SIDEPANEL_OWNERS_COUNT_TO_SHOW}`}
                  size='small'
                />
                <DSLink
                  text={intl.formatMessage({
                    id: 'Yw1pQe',
                    defaultMessage: 'View All',
                    description: 'View All',
                  })}
                  kind='button'
                  isDisabled={isDisabled}
                  onClick={() => {
                    dispatch(
                      serviceIdentityActions.setActionManager({
                        isOpen: true,
                        id: serviceId,
                        action: A_KEY_OS,
                        currentOwners: data.owners,
                      })
                    );
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        <Box
          tag='div'
          className={cx(
            'SummaryContent-policyContainer',
            ownersCount > 0 && 'SummaryContent-policyContainer--isBorder'
          )}>
          <Typography kind='h3' color={CF_SEC_BLD}>
            <FormattedMessage
              id='YxFBj3'
              defaultMessage='Password Policy'
              description='Password policy heading label'
            />
          </Typography>
          <Box tag='div' className='SummaryContent-policySelection'>
            <MenuInput
              isDisabled={isDisabled}
              options={policyOptions}
              onChange={(
                selectedOption:
                  | (MenuMultiOption & { description: string | null })
                  | null
              ) => {
                setPolicyOptions((prev) =>
                  prev.map((option) => ({
                    ...option,
                    isChecked: option.value === selectedOption?.value,
                  }))
                );
                setCurrentData((prevData) => {
                  if (prevData === null) {
                    return prevData;
                  }

                  if (!selectedOption || !('value' in selectedOption)) {
                    return {
                      ...prevData,
                      passwordPolicy: null,
                    };
                  }

                  if (!prevData.passwordPolicy) {
                    return {
                      ...prevData,
                      passwordPolicy: {
                        id: Number(selectedOption.value),
                        name: selectedOption.label,
                        description: selectedOption.description ?? '',
                      },
                    };
                  }

                  return {
                    ...prevData,
                    passwordPolicy: {
                      ...prevData.passwordPolicy,
                      id: Number(selectedOption.value),
                      name: selectedOption.label,
                      description: selectedOption.description ?? '',
                    },
                  };
                });
              }}
              placeholder={intl.formatMessage({
                id: 'c6Wclv',
                defaultMessage: 'Select policy',
                description: 'Select policy',
              })}
              noDataText={
                policyNoDataText
                  ? intl.formatMessage(l[policyNoDataText])
                  : undefined
              }
              isLoading={isPolicyLoading}
              hasMoreOptions={policyCursorRef.current !== null}
              onScroll={handlePolicyScroll}
              suffixIcon={<Icon kind='Check' size='medium' />}
              isSearchDisabled
              loadingText={intl.formatMessage({
                id: '8jhZJ2',
                defaultMessage: 'Loading more results',
                description: 'Loading more results',
              })}
              onSearchTermChange={(v: string | null) => {
                setPolicySearchText(v ?? '');
                policyCursorRef.current = null;
                debouncedLoadPolicyOptions(v ?? '');
              }}
              menuItemHeightConfigs={{ height: '2rem' }}
            />
          </Box>
          <Box tag='div' className='SummaryContent-policyDetails'>
            <Box tag='div'>
              <Typography kind='body2' color={CF_NEU_STL}>
                <FormattedMessage
                  id='rJFXWN'
                  defaultMessage='Details'
                  description='Password policy Details label'
                />
              </Typography>
              <Typography kind='body2' color={CF_NEU_BLD} lineLimit={3}>
                {policyOptions.find((p) => p.isChecked)?.description ?? (
                  <FormattedMessage
                    id='YoUJI1'
                    defaultMessage='-'
                    description='Hyphen'
                  />
                )}
              </Typography>
            </Box>
            <Button
              kind='outlined'
              size='small'
              isDisabled={
                Boolean(hasSummaryChanges || isDisabled) ||
                typeof policyId !== 'number'
              }
              leftIcon={<Icon kind='Reset' />}
              onClick={() => {
                dispatch(
                  serviceIdentityActions.setActionManager({
                    isOpen: true,
                    id: serviceId,
                    action: A_KEY_RO,
                    policyId: Number(policyId),
                    currentServiceAccounts: data.serviceAccounts,
                  })
                );
              }}>
              <FormattedMessage
                id='Vu1hnp'
                defaultMessage='Rotate now'
                description='Password policy  Rotate now button label'
              />
            </Button>
          </Box>
        </Box>
        <Box
          tag='div'
          className={cx(
            'SummaryContent-ownersContainer',
            'SummaryContent-policyContainer--isBorder'
          )}>
          <Box tag='div' className='SummaryContent-ownersTitle'>
            <Typography kind='h3' color={CF_SEC_BLD}>
              <FormattedMessage
                id='KwWQBp'
                defaultMessage='Service Accounts ({serviceAccountCount})'
                description='Password policy  Rotate now button label'
                values={{ serviceAccountCount }}
              />
            </Typography>
            <DSLink
              text={intl.formatMessage({
                id: 'gGSE70',
                defaultMessage: 'Link Account',
                description: 'Link Account',
              })}
              kind='button'
              isDisabled={isDisabled}
              onClick={() => {
                dispatch(
                  serviceIdentityActions.setActionManager({
                    isOpen: true,
                    id: serviceId,
                    action: A_KEY_SA,
                    currentServiceAccounts: data.serviceAccounts,
                  })
                );
              }}
            />
          </Box>
          <Box tag='div' className='SummaryContent-ownersList'>
            {serviceAccountCount === 0 && (
              <Box tag='div' className='ServiceIdentitySP-intermediateWrapper'>
                <Box
                  tag='div'
                  className='ServiceIdentitySP-errorMessageWrapper'>
                  <Icon kind='AlertInfo' size='large' />
                  <Typography kind='body2' color={CF_NEU_BLD}>
                    <FormattedMessage
                      id='/uWvEF'
                      defaultMessage='No service account linked.'
                      description='Message shown when there are no service accounts linked to the service identity'
                    />
                  </Typography>
                </Box>
              </Box>
            )}
            {data.serviceAccounts
              .slice(0, SIDEPANEL_OWNERS_COUNT_TO_SHOW)
              .map((acc) => (
                <SidePanelAccountItem
                  key={acc.id}
                  serviceAccountName={acc.name}
                  serviceAccountEndpoint={acc.endpoint}
                  serviceAccountStatus={acc.status}
                  isDisabled={isDisabled}
                  handleUnlinkBtn={() => {
                    dispatch(
                      serviceIdentityActions.setActionManager({
                        isOpen: true,
                        id: serviceId,
                        action: A_KEY_SA_UN,
                        targetServiceAccount: acc,
                        currentServiceAccounts: data.serviceAccounts,
                      })
                    );
                  }}
                />
              ))}
          </Box>
          {serviceAccountCount - SIDEPANEL_OWNERS_COUNT_TO_SHOW > 0 && (
            <Box tag='div' className='SummaryContent-moreSection'>
              <ApplicationIcon
                number={`+${
                  serviceAccountCount - SIDEPANEL_OWNERS_COUNT_TO_SHOW
                }`}
                size='small'
              />
              <DSLink
                text={intl.formatMessage({
                  id: 'Yw1pQe',
                  defaultMessage: 'View All',
                  description: 'View All',
                })}
                isDisabled={isDisabled}
                kind='button'
                onClick={() => {
                  dispatch(
                    serviceIdentityActions.setActionManager({
                      isOpen: true,
                      id: serviceId,
                      action: A_KEY_SA,
                      currentServiceAccounts: data.serviceAccounts,
                    })
                  );
                }}
              />
            </Box>
          )}
        </Box>
      </>
    );
  }, [
    intl,
    isError,
    isLoading,
    data,
    isDisabled,
    description,
    debouncedDescription,
    policyOptions,
    isPolicyError,
    isPolicyLoading,
    handlePolicyScroll,
    hasSummaryChanges,
    policyId,
    onRefresh,
    setCurrentData,
    dispatch,
    serviceId,
    debouncedLoadPolicyOptions,
  ]);

  if (!serviceId) {
    return null;
  }

  return (
    <TabContent
      className='SummaryContent-wrapperContainer'
      value={activeTab}
      tabValue='1'>
      {renderBody()}
    </TabContent>
  );
}

SummaryContent.defaultProps = {
  serviceId: undefined,
  policyId: undefined,
  isDisabled: false,
};

export default memo(SummaryContent);
